// Canvas pointer input. Uses setPointerCapture on pointerdown so a drag that
// leaves the canvas surface (e.g. over a DOM panel) continues to deliver
// pointermove/up events to us — essential for drag-to-reposition UX.

export interface PointerEventData {
  x: number; // css pixels relative to canvas
  y: number;
  button: number;
  pointerId: number;
}

export type PointerHandler = (e: PointerEventData) => void;

export class InputManager {
  private moveHandlers = new Set<PointerHandler>();
  private downHandlers = new Set<PointerHandler>();
  private upHandlers = new Set<PointerHandler>();

  constructor(private canvas: HTMLCanvasElement) {
    canvas.addEventListener("pointermove", this.onMove);
    canvas.addEventListener("pointerdown", this.onDown);
    canvas.addEventListener("pointerup", this.onUp);
    canvas.addEventListener("pointercancel", this.onUp);
    // Prevent default browser long-press / context menu from cancelling drags
    // on touch, and avoid text selection on desktop when drag-slipping.
    canvas.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  private toLocal(e: PointerEvent): PointerEventData {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      button: e.button,
      pointerId: e.pointerId,
    };
  }

  private onMove = (e: PointerEvent) => {
    const data = this.toLocal(e);
    for (const h of this.moveHandlers) h(data);
  };
  private onDown = (e: PointerEvent) => {
    // Keep receiving this pointer's move/up events even if the cursor leaves
    // the canvas during the gesture.
    try { this.canvas.setPointerCapture(e.pointerId); } catch { /* older browsers */ }
    const data = this.toLocal(e);
    for (const h of this.downHandlers) h(data);
  };
  private onUp = (e: PointerEvent) => {
    try { this.canvas.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    const data = this.toLocal(e);
    for (const h of this.upHandlers) h(data);
  };

  onPointerMove(h: PointerHandler): () => void {
    this.moveHandlers.add(h);
    return () => this.moveHandlers.delete(h);
  }
  onPointerDown(h: PointerHandler): () => void {
    this.downHandlers.add(h);
    return () => this.downHandlers.delete(h);
  }
  onPointerUp(h: PointerHandler): () => void {
    this.upHandlers.add(h);
    return () => this.upHandlers.delete(h);
  }

  destroy(): void {
    this.canvas.removeEventListener("pointermove", this.onMove);
    this.canvas.removeEventListener("pointerdown", this.onDown);
    this.canvas.removeEventListener("pointerup", this.onUp);
    this.canvas.removeEventListener("pointercancel", this.onUp);
    this.moveHandlers.clear();
    this.downHandlers.clear();
    this.upHandlers.clear();
  }
}
